import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import AvatarScreen from './AvatarScreen';

/**
 * Standalone wrapper for AvatarScreen that can be used independently
 * without requiring FrontendView context
 * Can be used either as a component with props or as a route with URL params
 */
const StandaloneAvatarScreen = ({
  locationId: propLocationId,
  userId: propUserId,
  client_url = 'default',
  onBackOverride = null, // Optional custom back handler
  language = 'en' // Default language
}) => {
  const navigate = useNavigate();
  const urlParams = useParams();

  // Use URL params if available, otherwise use props
  const locationId = propLocationId || urlParams.locationId;
  const userId = propUserId || urlParams.userId;

  // State for data fetching
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [locationData, setLocationData] = useState(null);
  const [brandColor, setBrandColor] = useState('#127E10');
  const [brandFont, setBrandFont] = useState('Arial');
  const [captionSettings, setCaptionSettings] = useState(null);
  const [wayfinderBackButton, setWayfinderBackButton] = useState(null);
  const [wayfinderQRCodeSettings, setWayfinderQRCodeSettings] = useState(null);
  const [wayfinderMapPositionSettings, setWayfinderMapPositionSettings] = useState(null);
  const [wayfinderReplayPopupSettings, setWayfinderReplayPopupSettings] = useState(null);
  const [logoBeforeMap, setLogoBeforeMap] = useState({});
  const [selectedLanguage, setSelectedLanguage] = useState(language);
  const [replayModal, setReplayModal] = useState(false);

  // Caption state from localStorage
  const [isCaptionOn, setIsCaptionOn] = useState(() => {
    try {
      const stored = localStorage.getItem(`captionEnabled_${client_url || 'default'}`);
      return stored === 'true';
    } catch (error) {
      console.error('Error reading caption state from localStorage:', error);
      return false;
    }
  });

  // Save caption state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(`captionEnabled_${client_url || 'default'}`, isCaptionOn.toString());
    } catch (error) {
      console.error('Error saving caption state to localStorage:', error);
    }
  }, [isCaptionOn, client_url]);

  // Fetch location data and branding settings
  useEffect(() => {
    const fetchData = async () => {
      if (!locationId || !userId) {
        setError('Missing locationId or userId');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch location data
        console.log('🔵 Fetching location data for:', locationId);
        const locationResponse = await axios.get(
          `/api/wayfinder/locations/${locationId}`,
          { params: { language: selectedLanguage } }
        );

        if (locationResponse.data.success && locationResponse.data.data) {
          const locData = locationResponse.data.data;
          setLocationData(locData);
          console.log('✅ Location data loaded:', locData);

          // Get client_url from the response if available
          const actualClientUrl = locationResponse.data.clientUrl || client_url || 'default';
          console.log('🔵 Using client_url:', actualClientUrl);

          // Fetch branding settings
          console.log('🔵 Fetching branding settings for userId:', userId);
          const brandingResponse = await axios.get(
            `/api/wayfinder/branding-settings/${userId}`
          );



          if (brandingResponse.data.success) {
            const settings = brandingResponse.data.data;

            // Set back button settings - disable it for standalone mode
            setWayfinderBackButton({
              ...settings.backButton,
              
            });

            setWayfinderQRCodeSettings(settings.qrCode);
            setWayfinderMapPositionSettings(settings.mapPosition);
            setWayfinderReplayPopupSettings(settings.timeoutPopup);
            setLogoBeforeMap(settings?.logoBeforeMap || {});

            // Set caption settings from branding settings
            if (settings.captionSettings) {
              setCaptionSettings(settings.captionSettings);
              console.log('✅ Caption settings loaded from branding:', settings.captionSettings);
            } else {
              // Fallback to default caption settings if not in branding
              setCaptionSettings({
                textColor: "#ffffff",
                bgColor: "#000000",
                backgroundOpacity: 60,
                fontSize: "22",
                padding: "8",
                fontWeight: "600",
                bottom: "40",
                textShadowH: "2",
                textShadowV: "2",
                textShadowBlur: "4",
                textShadowColor: "#000000",
                lineHeight: "1.4",
                letterSpacing: "0",
                fontFamily: "Arial",
                transition: 2,
                buttonTop: 50,
                buttonLeft: 50,
                iconType: "caption",
                iconSize: 50,
                iconOnColor: "#4CAF50",
                iconOffColor: "#757575",
                status: false,
              });
            }

            console.log('✅ Branding settings loaded');
          }

          // Fetch brand color and font from user settings
          try {
            const userResponse = await axios.get(`/api/button/frontend/${actualClientUrl}`, {
              params: { language: selectedLanguage }
            });

            if (!userResponse.data.error) {
              setBrandColor(userResponse.data?.brandcolor || '#127E10');
              setBrandFont(userResponse.data?.brandFont || 'Arial');
              console.log('✅ Brand settings loaded');
            }
          } catch (err) {
            console.warn('⚠️ Could not fetch brand settings, using defaults');
          }
        } else {
          throw new Error('Failed to fetch location data');
        }

      } catch (err) {
        console.error('❌ Error fetching data:', err);
        setError(err.message || 'Failed to load location data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [locationId, userId, client_url, selectedLanguage]);

  // Default back handler - does nothing in standalone mode (disabled)
  const handleBack = () => {
    if (onBackOverride) {
      onBackOverride();
    } else {
      // In standalone mode opened from table, back button should do nothing
      console.log('🔵 Back button clicked but disabled in standalone mode');
      // Optionally, you could close the window/tab
      // window.close();
    }
  };

  // Dummy reset session handler - just close the modal for standalone mode
  const handleResetSession = async () => {
    console.log('🔄 Reset session called (standalone mode) - closing modal');
    // In standalone mode, just close the modal instead of going back
    setReplayModal(false);
  };

  // Timeout popup settings with defaults
  const timeoutPopupSettings = wayfinderReplayPopupSettings || {
    backgroundColor: "#FFFFFF",
    borderColor: brandColor || "#127E10",
    borderWidth: 3,
    borderRadius: 20,
    popupWidth: 500,
    popupPadding: 40,
    fontFamily: brandFont || "Arial",
    text: {
      content: "Are You Still There?",
      textAlign: "center",
      fontSize: 32,
      color: "#333333",
      fontWeight: 600,
      padding: 20,
    },
    subtitle: {
      content: "Returning to main menu in...",
      textAlign: "center",
      fontSize: 18,
      color: "#666666",
      fontWeight: 400,
      padding: 10,
    },
    countdown: {
      fontSize: 48,
      color: brandColor || "#127E10",
      fontWeight: 700,
      padding: 8,
    },
    button: {
      text: "Yes, I'm Here",
      backgroundColor: brandColor || "#127E10",
      textColor: "#FFFFFF",
      fontSize: 24,
      paddingTopBottom: 15,
      paddingLeftRight: 40,
      borderRadius: 8,
      borderWidth: 0,
      borderColor: "transparent",
      alignment: "center",
      containerPadding: 30,
      width: 200,
      minWidth: 120,
      hoverBackgroundColor: brandColor ? `${brandColor}CC` : "#127E10CC",
      hoverTextColor: "#FFFFFF",
      fontWeight: 600,
      shadow: {
        shadowDistance: 4,
        shadowSize: 12,
        shadowColor: "#00000033",
      },
      letterSpacing: 0,
      textTransform: "none",
      opacity: 1,
    },
    avatarScreen: {
      headerText: "What would you like to do next?",
      replayButton: {
        text: "Replay Directions",
        backgroundColor: brandColor || "#127E10",
        textColor: "#FFFFFF",
        borderRadius: "8",
        padding: "20",
        fontSize: "16",
        shadow: {},
      },
      backToSearchButton: {
        text: "Back to Search",
        backgroundColor: "#666666",
        textColor: "#FFFFFF",
        borderRadius: "8",
        padding: "20",
        fontSize: "16",
        shadow: {},
      },
      closeWindowButton: {
        text: "Close this Window",
        backgroundColor: "#999999",
        textColor: "#FFFFFF",
        borderRadius: "8",
        padding: "20",
        fontSize: "16",
        shadow: {},
      },
    },
  };

  // Loading state
  if (loading) {
    return (
      <div style={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#1a1a1a',
        color: '#fff',
        fontSize: '24px',
        fontFamily: brandFont || 'Arial'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>🗺️</div>
          <div>Loading location...</div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#1a1a1a',
        color: '#fff',
        fontSize: '18px',
        fontFamily: brandFont || 'Arial'
      }}>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
          <div style={{ marginBottom: '20px' }}>{error}</div>
          <button
            onClick={handleBack}
            style={{
              padding: '12px 24px',
              fontSize: '16px',
              backgroundColor: brandColor || '#127E10',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // No location data
  if (!locationData) {
    return (
      <div style={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#1a1a1a',
        color: '#fff',
        fontSize: '18px',
        fontFamily: brandFont || 'Arial'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>📍</div>
          <div>Location not found</div>
        </div>
      </div>
    );
  }

  // Render AvatarScreen with all required props
  return (
    <AvatarScreen
      userId={userId}
      client_url={client_url}
      brandColor={brandColor}
      brandFont={brandFont}
      onBack={handleBack}
      locationData={locationData}
      backButtonSettings={wayfinderBackButton}
      timeoutPopupSettings={timeoutPopupSettings}
      replayPopupSettings={wayfinderReplayPopupSettings}
      logoBeforeMap={logoBeforeMap}
      qrCodeSettings={wayfinderQRCodeSettings}
      mapPositionSettings={wayfinderMapPositionSettings}
      language={selectedLanguage}
      captionSettings={captionSettings || {
        textColor: "#ffffff",
        bgColor: "#000000",
        backgroundOpacity: 60,
        fontSize: "22",
        padding: "8",
        fontWeight: "600",
        bottom: "40",
        textShadowH: "2",
        textShadowV: "2",
        textShadowBlur: "4",
        textShadowColor: "#000000",
        lineHeight: "1.4",
        letterSpacing: "0",
        fontFamily: "Arial",
        transition: 2,
        buttonTop: 50,
        buttonLeft: 50,
        iconType: "caption",
        iconSize: 50,
        iconOnColor: "#4CAF50",
        iconOffColor: "#757575",
        status: false,
      }}
      isCaptionOn={isCaptionOn}
      setIsCaptionOn={setIsCaptionOn}
      cameFromSearch={false} // Standalone mode - not from search
      resetSession={handleResetSession}
      selectedLanguage={selectedLanguage}
      videoList={[]} // Empty video list for standalone mode
      autoCloseReplayPopup={false} // Show replay popup normally in standalone mode
      replayModal={replayModal}
      setReplayModal={setReplayModal}
    />
  );
};

export default StandaloneAvatarScreen;
